// Refactored PricingContext.tsx with centralized modal state
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  AccountSubscriptionPlan,
  Invoice,
  IPricingContext,
  PaymentPlanDetails,
  paymentPlans,
  PopupDataType,
  Product,
  Subscription,
} from "../PaymentPlanResources";
import FT from "../../../libs/FT";
import { userProfile } from "../../../libs/UserProfile";

const PricingContext = createContext<IPricingContext | null>(null);

export const usePricingContext = () => {
  const context = useContext(PricingContext);
  if (!context)
    throw new Error("usePricingContext must be used within PricingProvider");
  return context;
};

const usePaymentProducts = (paymentPlanDetails: PaymentPlanDetails) => {
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [loadingPackages, setLoadingPackages] = useState<
    "not_loading" | "loading" | "loaded"
  >("not_loading");

  const fetchProduct = async (productName: string, productId?: string) => {
    if (!productId) return;
    if (Object.keys(products).length === 0) {
      setLoadingPackages("loading");
    }
    try {
      const productResult = await FT.getCreditPaymentInfo(
        "productInfo",
        `productId=${productId}&currency=${
          paymentPlanDetails.selectedPaymentPlan.currencyId || "usd"
        }&productName=${productName}`
      );
      setProducts((prev) => {
        const stripeProducts = { ...prev, [productName]: productResult };

        if (stripeProducts["tublianProPlan"]) {
          setLoadingPackages("loaded");
        }

        return stripeProducts;
      });
    } catch (err) {
      // console.error("Product fetch error:", err);
    } finally {
      setLoadingPackages("loaded");
    }
  };

  return { products, fetchProduct, loadingPackages };
};

export const PricingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const user = userProfile(localStorage);
  const [submitted, setSubmitted] = useState(false);
  const [paymentPlanDetails, setPaymentPlanDetails] =
    useState<PaymentPlanDetails>({
      billingFreq: "monthly",
      selectedPaymentPlan: paymentPlans[1],
    });

  const [accountSubscriptionPlan, setAccountSubscriptionPlan] =
    useState<AccountSubscriptionPlan | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const { products, fetchProduct, loadingPackages } =
    usePaymentProducts(paymentPlanDetails);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [paymentStatusPopup, setPaymentStatusPopup] = useState<PopupDataType>({
    isActive: false,
    msg: "",
    status: null,
  });
  const [paymentStatus, setPaymentStatus] = useState<
    "success" | "error" | "loading" | null
  >(null);
  const [nextBillingDate, setNextBillingDate] = useState<Date | null>(null);
  const [invoices, setInvoices] = useState<
    (Invoice & { subscriptionPlan: string; billingInterval: string })[]
  >([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  const [modalState, setModalState] = useState({
    showChangePlan: false,
    showCancelPlanToast: false,
    showChangePlanToast: false,
    showPlanFeedback: false,
    showPaymentPage: false,
    showAllInvoices: false,
    cancelSubPlan: false,
  });

  const toggleModal = (key: keyof typeof modalState, value: boolean) => {
    setModalState((prev) => ({ ...prev, [key]: value }));
  };

  const updatePlanDetails = (field: keyof PaymentPlanDetails, value: any) => {
    setPaymentPlanDetails((prev) => ({ ...prev, [field]: value }));
  };
  // console.log("product", products);
  // console.log("accountSubscriptionPlan", accountSubscriptionPlan);
  const fetchProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const response = await FT.get(user.userId(), {});
      // console.log("response", response);

      if (response?.accountSubscriptionPlan) {
        setAccountSubscriptionPlan(response.accountSubscriptionPlan);
      } else {
        setAccountSubscriptionPlan({
          subscriptionPlan: "Free",
          billingInterval: "",
          amountPaid: 0,
          subscriptionId: "",
          customerId: "",
          currency: "",
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line
  }, []);

  const getPrice = (productName: string | null, interval: string) => {
    if (!productName) return null;
    const product = products[productName];
    const prices = product?.prices;
    if (!Array.isArray(prices)) return null;

    return (
      prices.find((price) => price.recurring.interval === interval) || null
    );
  };

  const getDisplayAmount = (unitAmount: number) =>
    (unitAmount / 100).toFixed(2);

  async function fetchWithRetry(url: string, retries = 5, delay = 1000) {
    for (let i = 0; i < retries; i++) {
      const res: any = await FT.getCreditPaymentInfo(
        "subscriptionPaymentHistoryInfo",
        url
      );

      if (!res?.error || res.error !== "Customer not found") {
        return res;
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    throw new Error("Customer not found after retries");
  }

  const paymentHistory = async (useRetry: boolean = true) => {
    if (!accountSubscriptionPlan?.customerId) {
      // console.warn("No customerId yet, skipping paymentHistory");
      return;
    }

    setIsLoadingInvoices(true);
    try {
      let res: Subscription[];

      if (useRetry) {
        res = await fetchWithRetry(
          `customerId=${accountSubscriptionPlan?.customerId}`,
          5,
          1000
        );
      } else {
        res = await FT.getCreditPaymentInfo(
          "subscriptionPaymentHistoryInfo",
          `customerId=${accountSubscriptionPlan?.customerId}`
        );
      }

      const activeSubscription = res?.find((sub) =>
        ["active", "trialing"].includes(sub.status)
      );

      if (activeSubscription?.currentPeriodEnd) {
        setNextBillingDate(new Date(activeSubscription.currentPeriodEnd));
      }

      if (activeSubscription?.paymentMethod && accountSubscriptionPlan) {
        setAccountSubscriptionPlan((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            paymentMethod:
              typeof activeSubscription.paymentMethod === "string"
                ? { brand: "", last4: activeSubscription.paymentMethod }
                : activeSubscription.paymentMethod,
          };
        });
      }

      const flatInvoices = res.flatMap((sub: Subscription) =>
        sub.invoices.map((inv: Invoice) => ({
          ...inv,
          subscriptionPlan: sub.subscriptionPlan,
          billingInterval: sub.billingInterval,
        }))
      );

      setInvoices(flatInvoices);
    } catch (error) {
      // console.error("Failed to fetch payment history:", error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  useEffect(() => {
    fetchProduct(
      "tublianProPlan",
      process.env.REACT_APP_TUBLIAN_PRO_PLAN_PRODUCT_ID
    );
    // fetchProduct(
    //   "tublianFreelancerPlan",
    //   process.env.REACT_APP_TUBLIAN_FREELANCER_PLAN_PRODUCT_ID
    // );
    // eslint-disable-next-line
  }, []);

  const contextValue: IPricingContext = {
    paymentPlanDetails,
    setPaymentPlanDetails,
    updatePlanDetails,
    submitted,
    setSubmitted,
    accountSubscriptionPlan,
    setAccountSubscriptionPlan,
    fetchProduct,
    products,
    getPrice,
    getDisplayAmount,
    errorMessage,
    setErrorMessage,
    loadingSubscription,
    setLoadingSubscription,
    loadingPackages,
    paymentStatusPopup,
    setPaymentStatusPopup,
    nextBillingDate,
    setNextBillingDate,
    invoices,
    setInvoices,
    paymentHistory,
    paymentStatus,
    setPaymentStatus,
    modalState,
    toggleModal,
    isLoadingInvoices,
    setIsLoadingInvoices,
    fetchProfile,
    isLoadingProfile,
  };

  return (
    <PricingContext.Provider value={contextValue}>
      {children}
    </PricingContext.Provider>
  );
};
